import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import FirstLoginPage from './pages/FirstLoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import VehiclesPage from './pages/VehiclesPage';
import MaintenancesPage from './pages/MaintenancesPage';
import ReservationsPage from './pages/ReservationsPage';
import ContractsPage from './pages/ContractsPage';
import InvoicesPage from './pages/InvoicesPage';
import FinanceiroPage from './pages/FinanceiroPage';
import ReportsPage from './pages/ReportsPage';
import ReportsMenuPage from './pages/ReportsMenuPage';
import ContractsReportPage from './pages/reports/ContractsReportPage';
import MaintenancesReportPage from './pages/reports/MaintenancesReportPage';
import VehiclesReportPage from './pages/reports/VehiclesReportPage';
import RentabilidadeReportPage from './pages/reports/RentabilidadeReportPage';
import WeeklyReportPage from './pages/reports/WeeklyReportPage';
import ClosingsHistoryPage from './pages/ClosingsHistoryPage';
import TemplatesPage from './pages/TemplatesPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import NotificacoesPage from './pages/NotificacoesPage';
import MockupsAutomacao from './pages/MockupsAutomacao';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AuthenticatedRoute from './components/AuthenticatedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

export const useAuth = () => React.useContext(AuthContext);

// Auth Callback Component for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
            withCredentials: true
          });
          
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user: response.data.user }, replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/login', { replace: true });
        }
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/primeiro-acesso" element={<AuthenticatedRoute><FirstLoginPage /></AuthenticatedRoute>} />
      <Route path="/reset-password" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <AuthenticatedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute module="dashboard">
                    <DashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/clientes" element={
                  <ProtectedRoute module="clientes">
                    <ClientsPage />
                  </ProtectedRoute>
                } />
                <Route path="/veiculos" element={
                  <ProtectedRoute module="veiculos">
                    <VehiclesPage />
                  </ProtectedRoute>
                } />
                <Route path="/manutencoes" element={
                  <ProtectedRoute module="manutencoes">
                    <MaintenancesPage />
                  </ProtectedRoute>
                } />
                <Route path="/reservas" element={
                  <ProtectedRoute module="reservas">
                    <ReservationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/contratos" element={
                  <ProtectedRoute module="contratos">
                    <ContractsPage />
                  </ProtectedRoute>
                } />
                <Route path="/faturamento" element={
                  <ProtectedRoute module="faturamento">
                    <InvoicesPage />
                  </ProtectedRoute>
                } />
                <Route path="/notificacoes" element={
                  <ProtectedRoute module="faturamento">
                    <NotificacoesPage />
                  </ProtectedRoute>
                } />
                <Route path="/mockups/automacao" element={
                  <ProtectedRoute module="faturamento">
                    <MockupsAutomacao />
                  </ProtectedRoute>
                } />
                <Route path="/financeiro" element={
                  <ProtectedRoute module="financeiro">
                    <FinanceiroPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios" element={
                  <ProtectedRoute module="relatorios">
                    <ReportsMenuPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/financeiros" element={
                  <ProtectedRoute module="relatorios">
                    <ReportsPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/fechamentos" element={
                  <ProtectedRoute module="relatorios">
                    <ClosingsHistoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/contratos" element={
                  <ProtectedRoute module="relatorios">
                    <ContractsReportPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/manutencoes" element={
                  <ProtectedRoute module="relatorios">
                    <MaintenancesReportPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/veiculos" element={
                  <ProtectedRoute module="relatorios">
                    <VehiclesReportPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/rentabilidade" element={
                  <ProtectedRoute module="relatorios">
                    <RentabilidadeReportPage />
                  </ProtectedRoute>
                } />
                <Route path="/relatorios/semanal" element={
                  <ProtectedRoute module="relatorios">
                    <WeeklyReportPage />
                  </ProtectedRoute>
                } />
                <Route path="/documentos" element={
                  <ProtectedRoute module="documentos">
                    <TemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="/configuracoes" element={
                  <ProtectedRoute module="configuracoes">
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminPage />
                  </ProtectedRoute>
                } />
                <Route path="/perfil" element={
                  <AuthenticatedRoute>
                    <ProfilePage />
                  </AuthenticatedRoute>
                } />
              </Routes>
            </Layout>
          </AuthenticatedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, logout, API }}>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
